// src/components/PanhandleMap.js
// Interactive Leaflet map of the 11-county Nebraska Panhandle region.
// Designed for Chapter 1 of the PADD 2026–2031 CEDS.

import { useEffect } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
// Generated by scripts/build-panhandle-communities.js from U.S. Census Bureau
// TIGERweb (ACS26 incorporated places, Jan 1 2026) with 2020 Decennial Census
// population. Re-run that script to refresh; do not edit the JSON by hand.
import communityData from "../data/panhandle-communities.json";
// Same script; TIGERweb ACS26 county boundaries for the 11 PADD counties.
import countyBoundaries from "../data/panhandle-counties.geo.json";

// Fix Leaflet's default marker icon paths (webpack asset pipeline issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// ── 11-COUNTY PANHANDLE — ALL INCORPORATED COMMUNITIES ───────────────────────
// Every incorporated city and village, plus Harrisburg — the Banner County
// seat, which is unincorporated (Census-designated place).
const COMMUNITIES = communityData.communities;

const NAVY  = "#122326";
const TERRA = "#A84F30";

// County outlines: context only, never competing with the community circles
// or basemap labels — thin muted line, near-transparent fill.
const COUNTY_STYLE = {
  color:       "#68736f",
  weight:      1,
  opacity:     0.45,
  fillColor:   "#9aa39f",
  fillOpacity: 0.035,
};

// Circle radius in SCREEN PIXELS — circleMarker uses px, not meters.
// Square-root scale from a visible floor, so a village of a few dozen people
// still gets a clear dot and Scottsbluff doesn't dwarf everything else.
// Range: 4px (Clinton, pop. 38; also used when population is unavailable)
// to 14px (Scottsbluff, pop. 14,436).
const MIN_RADIUS = 4;
const MAX_RADIUS = 14;
const MAX_POP = Math.max(...COMMUNITIES.map((c) => c.population || 0));
function getRadius(population) {
  if (!population) return MIN_RADIUS;
  return MIN_RADIUS + (MAX_RADIUS - MIN_RADIUS) * Math.sqrt(population / MAX_POP);
}

// Permanent name labels appear only once the reader zooms in far enough
// for them not to overlap (the Scottsbluff–Gering–Terrytown cluster).
const LABEL_MIN_ZOOM = 9;

const TYPE_LABEL = { city: "City", village: "Village", cdp: "Unincorporated (Census-designated place)" };

const fmtPop = (p) => (p == null ? "not available" : p.toLocaleString());

function detailsHtml(t) {
  return `
    <div style="font-family:var(--font-sans,sans-serif);font-size:13px;line-height:1.5;min-width:140px">
      <div style="font-weight:700;font-size:14px;color:${NAVY};margin-bottom:4px">
        ${t.name}${t.countySeat ? " <span style='color:" + TERRA + "'>★</span>" : ""}
      </div>
      <div style="color:#555">${t.county} County</div>
      <div style="color:#555">${TYPE_LABEL[t.type] || t.type}</div>
      <div style="color:#555">Population: ${fmtPop(t.population)} <span style="color:#999;font-size:11px">(2020 Census)</span></div>
      ${t.countySeat ? `<div style="margin-top:4px;font-size:11px;color:${TERRA};font-weight:600">County Seat</div>` : ""}
    </div>
  `;
}

export default function PanhandleMap() {
  useEffect(() => {
    const container = document.getElementById("panhandle-region-map");
    if (!container) return;

    const map = L.map(container, {
      // Map auto-fits all communities. Zoom buttons, double-click, pinch and
      // drag let readers find small towns; scroll-wheel zoom stays off so the
      // page scrolls normally past the map.
      zoomControl:      true,
      scrollWheelZoom:  false,
      doubleClickZoom:  true,
      dragging:         true,
      keyboard:         true,
      touchZoom:        true,
      // Placeholder center/zoom overridden immediately by fitBounds below
      center: [41.9, -102.5],
      zoom: 7,
    });

    // OpenStreetMap standard tiles — no API key required (CARTO's basemap now
    // returns "API KEY REQUIRED" tiles). OSM's tile policy requires this attribution.
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    // Added before the circles so it draws underneath them; non-interactive so
    // it never intercepts hover/click on a community.
    L.geoJSON(countyBoundaries, { style: COUNTY_STYLE, interactive: false }).addTo(map);

    const labels = L.layerGroup();

    // Draw larger circles first so small towns sit on top and stay clickable
    [...COMMUNITIES].sort((a, b) => (b.population || 0) - (a.population || 0)).forEach((t) => {
      const isSeat = t.countySeat;
      const r = getRadius(t.population);
      const circle = L.circleMarker([t.latitude, t.longitude], {
        radius:      r,
        color:       isSeat ? TERRA  : NAVY,
        fillColor:   isSeat ? TERRA  : NAVY,
        fillOpacity: isSeat ? 0.85   : 0.55,
        weight:      isSeat ? 2      : 1,
      }).addTo(map);

      circle.bindTooltip(detailsHtml(t), { direction: "top", offset: [0, -r] });
      circle.bindPopup(detailsHtml(t));

      L.marker([t.latitude, t.longitude], {
        interactive: false,
        keyboard: false,
        icon: L.divIcon({
          className: "community-label",
          html: `<span style="margin-left:${r + 3}px">${t.name}</span>`,
          iconSize: [0, 0],
        }),
      }).addTo(labels);
    });

    const toggleLabels = () => {
      if (map.getZoom() >= LABEL_MIN_ZOOM) labels.addTo(map);
      else labels.remove();
    };
    map.on("zoomend", toggleLabels);

    // Auto-fit to show all communities with comfortable padding
    const bounds = L.latLngBounds(COMMUNITIES.map((t) => [t.latitude, t.longitude]));
    map.fitBounds(bounds, { padding: [32, 32] });
    toggleLabels();

    return () => { map.remove(); };
  }, []);

  return (
    <div style={{ margin: "32px 0" }}>
      {/* Map container */}
      <div
        id="panhandle-region-map"
        style={{
          width: "100%",
          height: "460px",
          borderRadius: "10px",
          border: "1px solid #E2E0DB",
          overflow: "hidden",
        }}
      />

      {/* Legend */}
      <div style={{
        display: "flex",
        gap: "24px",
        flexWrap: "wrap",
        alignItems: "center",
        marginTop: "10px",
        fontSize: "12px",
        color: "#5A5A5A",
      }}>
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <svg width="14" height="14" viewBox="0 0 14 14">
            <circle cx="7" cy="7" r="6" fill={TERRA} fillOpacity="0.85" stroke={TERRA} strokeWidth="1.5" />
          </svg>
          County seat
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <svg width="14" height="14" viewBox="0 0 14 14">
            <circle cx="7" cy="7" r="5" fill={NAVY} fillOpacity="0.55" stroke={NAVY} strokeWidth="1" />
          </svg>
          Other incorporated city or village
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <svg width="20" height="14" viewBox="0 0 20 14">
            <circle cx="4"  cy="7" r="3"  fill={NAVY} fillOpacity="0.5" />
            <circle cx="15" cy="7" r="6"  fill={NAVY} fillOpacity="0.5" />
          </svg>
          Circle size = population (2020 Census)
        </span>
        <span style={{ marginLeft: "auto", fontStyle: "italic", color: "#9ca3af", fontSize: "11px" }}>
          {COMMUNITIES.length} communities · hover or click a circle; zoom in for names
        </span>
      </div>
      <p style={{ margin: "6px 0 0", fontSize: "11px", color: "#9ca3af", lineHeight: 1.5 }}>
        Every incorporated city and village in the 11 counties. Harrisburg, the
        Banner County seat, is unincorporated and is shown because it is a county
        seat. Source: U.S. Census Bureau, TIGERweb incorporated places (January 1,
        2026); population from the 2020 Decennial Census.
      </p>
    </div>
  );
}
